/**
 * COPY STRATEGY — FR0457FormPage
 *
 * Awareness:      Solution-Aware (Stage 3/5)
 *                 Reader already decided to use actual expenses and knows they
 *                 need the FR0457 form. Needs a step-by-step how-to.
 *
 * Sophistication: Stage 2 — Enlarged Claim
 *                 "Per 5 min" — speed claim. "Atrakinkite kuro ir remonto
 *                 išlaidas" — unlocking savings as the reward for completing the form.
 *
 * Avatar:         Driver ready to switch from 30% norm to actual expenses.
 *                 Mid-experience, already decided, needs the specific VMI form.
 *                 Slightly intimidated by bureaucracy but motivated by savings.
 *
 * Hook:           Simplicity + reward — "per 5 min: atrakinkite kuro ir remonto išlaidas"
 * Structure:      Why this form matters → Prerequisites → Step-by-step → CTA
 * Proof:          Exact VMI portal steps, field-by-field instructions
 * Funnel:         Feeds from ExpenseNormPage and AdvancedExpenseStrategyPage
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiInfo } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const FR0457FormPage = () => {
  usePageSetup('FR0457 forma per 5 min: atrakinkite kuro ir remonto išlaidas', {
    scroll: true,
    description: 'Žingsnis po žingsnio: kaip užpildyti FR0457 formą per VMI portalą ir deklaruoti automobilį kaip verslo turtą. Atrakinkite kuro, remonto ir draudimo išlaidų atskaitymus.',
    type: 'article',
    datePublished: '2026-01-22',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText
          as="h1"
          className="text-zinc-900 text-2xl font-medium mt-3 mb-2"
        >
          FR0457 forma per 5 min: atrakinkite kuro ir remonto išlaidas
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">VMI</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">5 min skaitymo</span>
        </MotionDiv>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-2"
        >
          Ši viena forma gali sutaupyti jums <strong>šimtus eurų per metus</strong> – nes leidžia atskaityti kuro, remonto ir draudimo išlaidas.
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-base mb-6"
        >
          Užpildymas užtrunka 5 minutes. Štai kaip tai padaryti teisingai.
        </MotionText>

        <MotionDiv className="p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-xl mb-4">
          <div className="flex gap-3">
            <PiInfo className="min-w-6 h-6 text-blue-700" />
            <div>
              <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
                Galite nurodyti praeitą datą
              </MotionText>
              <MotionText as="p" className="text-blue-800 text-sm">
                Jei jau dirbate kaip pavežėjas, galite deklaruoti automobilį su <strong>praeities data</strong>. Svarbu – pateikite formą iki metinės GPM deklaracijos (gegužės 2 d.).
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kas yra FR0457 forma?
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-4"
        >
          <strong>FR0457</strong> – tai „Pranešimas apie ilgalaikio turto naudojimą" individualioje veikloje. Ši forma praneša VMI, kad naudojate savo automobilį verslo tikslais ir norite:
        </MotionText>

        <MotionDiv className="space-y-2 mb-8">
          <MotionDiv className="flex items-start gap-2">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Atskaityti kuro, remonto, draudimo išlaidas
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex items-start gap-2">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Skaičiuoti automobilio nusidėvėjimą
            </MotionText>
          </MotionDiv>
          <MotionDiv className="flex items-start gap-2">
            <PiCheckCircle className="min-w-5 h-5 text-green-600 mt-0.5" />
            <MotionText as="p" className="text-zinc-700 text-sm">
              Legaliai sumažinti apmokestinamąjį pelną
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Kiekvieno lauko paaiškinimas
        </MotionText>

        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-6"
        >
          Štai ką įrašyti į kiekvieną FR0457 formos laukelį:
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-8">
          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              4 laukelis: Turto rūšies kodas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Įrašykite skaičių pagal automobilio tipą:
            </MotionText>
            <MotionDiv className="grid grid-cols-2 gap-2">
              <MotionDiv className="p-2 bg-white border border-zinc-250 rounded-lg">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">17</MotionText>
                <MotionText as="p" className="text-zinc-600 text-xs">Lengvasis automobilis</MotionText>
              </MotionDiv>
              <MotionDiv className="p-2 bg-white border border-zinc-250 rounded-lg">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">18</MotionText>
                <MotionText as="p" className="text-zinc-600 text-xs">Krovininis, autobusas</MotionText>
              </MotionDiv>
            </MotionDiv>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              5 laukelis: Turto identifikavimo duomenys
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Įrašykite automobilio <strong>VIN numerį</strong> (kėbulo numerį) iš registracijos dokumentų. Jei VIN nėra – valstybinį numerį.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              6 laukelis: Naudojimo dalis procentais
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Kiek procentų automobilis naudojamas <strong>verslui</strong> (pvz., 50, 80 ar 100).
            </MotionText>
            <MotionDiv className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-2">
                <PiInfo className="min-w-5 h-5 text-blue-600" />
                <MotionText as="p" className="text-blue-800 text-xs">
                  <strong>Patarimas:</strong> Rekomenduojame nurodyti 90-95%, ne 100%. VMI supranta, kad automobilį naudojate ir asmeniniams tikslams (parduotuvė, šeima). 100% gali sukelti papildomų klausimų.
                </MotionText>
              </div>
            </MotionDiv>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              7 laukelis: Sutuoktinio sutikimas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm mb-2">
              Pasirinkite vieną:
            </MotionText>
            <MotionDiv className="grid grid-cols-2 gap-2">
              <MotionDiv className="p-2 bg-white border border-zinc-250 rounded-lg">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">T (Taip)</MotionText>
                <MotionText as="p" className="text-zinc-600 text-xs">Automobilis – bendra nuosavybė ir sutuoktinis sutinka</MotionText>
              </MotionDiv>
              <MotionDiv className="p-2 bg-white border border-zinc-250 rounded-lg">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium">N (Ne)</MotionText>
                <MotionText as="p" className="text-zinc-600 text-xs">Nesate susituokę arba auto – asmeninis turtas</MotionText>
              </MotionDiv>
            </MotionDiv>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              8 laukelis: Ar bus laikomasi normatyvų?
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Pasirinkite <strong>T (Taip)</strong> – tai leidžia skaičiuoti nusidėvėjimą pagal oficialias normas ir sumažinti apmokestinamąjį pelną.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              9 laukelis: Turto pavadinimas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Įrašykite automobilio <strong>markę ir modelį</strong>, pvz., „Toyota Prius“ arba „Volvo XC60“.
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              10 laukelis: Naudojimo pradžios data
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Data, nuo kada pradedate naudoti automobilį verslui. Formatas: <strong>YYYY-MM-DD</strong> (pvz., 2025-01-15).
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-2">
              11 laukelis: Įsigijimo kaina
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Automobilio pirkimo kaina <strong>eurais</strong>, suapvalinta iki sveiko skaičiaus (be centų).
            </MotionText>
          </div>

          <div className="border-t border-zinc-200" />

          <div className="p-4 bg-green-50">
            <MotionText as="p" className="text-green-900 text-sm font-medium mb-2">
              12 ir 13 laukeliai: Palikite tuščius
            </MotionText>
            <MotionText as="p" className="text-green-800 text-sm">
              Šie laukeliai pildomi tik tada, kai <strong>baigiama naudoti</strong> arba <strong>parduodamas</strong> automobilis. Dabar – palikite tuščius.
            </MotionText>
          </div>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Svarbūs patarimai
        </MotionText>

        <MotionDiv className="space-y-3 mb-8">
          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              Suapvalinkite kainas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              11 lauko suma turi būti suapvalinta iki artimiausio euro (be centų).
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              Saugokite dokumentus
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Pirkimo sąskaitos ir registracijos dokumentų įkelti nereikia, bet <strong>privalo būti saugomi</strong> VMI audito atveju.
            </MotionText>
          </MotionDiv>

          <MotionDiv className="p-4 border-l-4 border-zinc-300 bg-zinc-50 rounded-r-xl">
            <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-1">
              Patikrinkite klaidas
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm">
              Prieš pateikdami spauskite „Klaidų tikrinimas" – sistema patikrins, ar nepraleidote privalomų laukų.
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionText
          as="h2"
          className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2"
        >
          Trumpa instrukcija
        </MotionText>

        <MotionDiv className="p-4 bg-zinc-900 rounded-xl mb-4">
          <MotionText as="p" className="text-zinc-50 text-sm font-medium mb-4">
            Greitas užpildymo gidas:
          </MotionText>
          <MotionDiv className="space-y-2">
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">4:</span> Įrašykite <span className="text-green-400 font-medium">17</span> (lengvasis auto)
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">5:</span> Įrašykite <span className="text-green-400 font-medium">VIN numerį</span>
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">6:</span> Įrašykite <span className="text-green-400 font-medium">90-95</span> (procentai verslui)
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">7:</span> Pasirinkite <span className="text-green-400 font-medium">T</span> arba <span className="text-green-400 font-medium">N</span>
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">8:</span> Pasirinkite <span className="text-green-400 font-medium">T</span>
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">9:</span> Įrašykite <span className="text-green-400 font-medium">markę ir modelį</span>
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">10:</span> Įrašykite <span className="text-green-400 font-medium">datą</span> (YYYY-MM-DD)
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">11:</span> Įrašykite <span className="text-green-400 font-medium">kainą eurais</span>
            </MotionText>
            <MotionText as="p" className="text-zinc-300 text-sm">
              <span className="text-zinc-50 font-medium">12-13:</span> Palikite <span className="text-amber-400 font-medium">tuščius</span>
            </MotionText>
          </MotionDiv>
          <MotionDiv className="mt-4 pt-4 border-t border-zinc-700">
            <MotionText as="p" className="text-zinc-300 text-xs">
              Užpildę spauskite „Klaidų tikrinimas", tada – „Pateikti deklaraciją"
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-blue-50 border border-blue-200 rounded-xl mb-4">
          <MotionText as="h3" className="text-blue-900 text-sm font-medium mb-2">
            Kas toliau?
          </MotionText>
          <MotionText as="p" className="text-blue-800 text-sm mb-3">
            Pateikus FR0457 formą, galite pradėti deklaruoti automobilio išlaidas. Sužinokite, kaip maksimaliai sumažinti mokesčius su faktinėmis išlaidomis:
          </MotionText>
          <ButtonLink variant="secondary" to="/blog/60-procentu-islaidos-strategija">
            Skaityti: 60% faktinių išlaidų strategija
          </ButtonLink>
        </MotionDiv>

        <FinhudasCTA awareness="solution" />

        <RelatedArticles articles={[
          { slug: '60-procentu-islaidos-strategija', title: '60% faktinės išlaidos: strategija €45k pajamoms' },
          { slug: 'kuro-apskaita-3-metodai-islaidos', title: 'Kuro išlaidos: 3 metodai' },
          { slug: 'individualios-veiklos-islaidos-30-procentu-arba-faktines', title: '30% norma ar faktinės išlaidos?' }
        ]} />

        <motion.div
          className="mt-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <ButtonLink variant="secondary" to="/blog">
            Grįžti į straipsnius
          </ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default FR0457FormPage
